// Payouts → the visible glossary (founder, 3 Sep: "all are magic labels").
//
// Hover tooltips are hidden help. Every payout screen carries this instead:
// an always-visible affordance that opens the screen's own vocabulary,
// explained in sentences — what each word means, where the number comes
// from, and what to do about it. One component so the terms never drift
// between screens.

import React from 'react';
import s from './Payouts.module.css';

export const TERMS = {
  earned: ['Earned (ungated)',
    'What the fleet’s screens earned from settled advertiser money before the day-gate was applied. '
    + 'Kept and shown deliberately, so a gated day is visible — never silently vanished.'],
  gate: ['Gate withheld / gated',
    'Money on days that missed the eligibility bar (the default gate — ≥4 trips + ≥4 passenger-hours from Oct 2026; ≥5 driving-hours before — per screen, per IST day). '
    + 'Withheld from the payout, listed day-by-day with its numbers on the Eligibility screen. Earned − gated = payable.'],
  payable: ['Payable / gross',
    'The month’s earnings that passed the gate, and what the statement starts from. It is the COLLECTION-side '
    + 'working — triggered by an advertiser invoice settling, with no passenger test in it — so it is not what we '
    + 'owe. What we owe is the tax invoice raised in the media owner’s name on presence-verified delivery, which '
    + 'is a different and smaller number (founder, 24 Sep).'],
  sim: ['SIM — recorded, not charged',
    'The per-device SIM cost (₹170/device/month today), still computed and still carried on the statement, but '
    + 'NOT recovered: "the invoicing is agnostic to SIM cost… no booking and recovering cycle now" (founder, '
    + '24 Sep). It stays on the ledger so that the day it IS charged, the change is a new outward document rather '
    + 'than a restatement of one already filed. Ungated by design — a parked day still burns its SIM.'],
  tds: ['TDS',
    'Tax withheld from the payout per the party’s agreement rate (194C for media/space, 194H for commissions). '
    + 'No rate is configured for any party today, and the invoice does not carry TDS maths (founder, 24 Sep) — '
    + 'the accounts team manages the liability when a transfer is actually made.'],
  carry: ['Carry-forward',
    'A net below ₹1,000 (or a negative month) rides forward inside the statement rather than paying out. It has '
    + 'never yet been used — every carry figure on record is zero — and it moves the statement’s residue, not the '
    + 'invoice: since SIM is no longer charged, the case it was written for (SIM exceeding gated earnings) can no '
    + 'longer arise. The published fleet terms still promise it.'],
  statement: ['Statement lifecycle',
    'The month close OPENS a statement per party and raises that media owner’s tax invoice in their name — there '
    + 'is no confirm step and no human approval (founder, 24 Sep). A statement is the engine’s working, not a '
    + 'proposal. The only human act left is MARK PAID: the accounts team records the bank reference and states '
    + 'the TDS they withheld on that transfer. What is owed is the invoice, never the statement’s residue.'],
  settled: ['Settled money',
    'Shares are computed only on advertiser spend that has actually settled into an invoice — the same event that '
    + 'fixes tax. Money a brand hasn’t settled yet is visible in the river as “accruing”, and earns nobody anything yet.'],
  waterfall: ['The split (waterfall)',
    'Every settled ₹1,000: ₹200 technical fees, then the ₹800 pool splits ₹240 premise operator / ₹200 device '
    + 'owner / ₹360 pDOOH. A partner’s staged commission (8→15% of the brand’s monthly book) is paid out of '
    + 'pDOOH’s ₹360 — fleet shares never move because a partner exists.'],
  premise: ['Premise operator',
    'Who operates the place the screen lives — today, the cab fleet (CabE). Keyed on the cab node; earns 30% of the pool.'],
  deviceOwner: ['Device owner',
    'Who owns the screen itself. Often the same party as the premise operator, but the roles stay separate so an '
    + 'investor’s screens can ride in another fleet. Earns 25% of the pool.'],
  house: ['pDOOH (house)',
    'Any role no external party holds is pDOOH’s, printed as an ordinary line — technical fees, margin, and the '
    + 'supply shares of our own demo/bench devices. Never hidden inside a residual.'],
  eligibility: ['Day eligibility',
    'Each screen’s day is judged once against the gate, frozen with the trips and hours it actually produced, and '
    + 'never recomputed. The verdict gates both supply shares for that day.'],
  ownership: ['Ownership history',
    'Who owned each cab and screen, as dated intervals. Money resolves the owner as of the play-day and stamps it '
    + 'forever — transferring a screen never rewrites who earned the past.'],
  recon: ['Reconciliation',
    'The identities that must hold or the screen is lying: every split invoice’s lines sum back to its settled base '
    + 'to the paise; statements equal the lines they consumed; close-time partner reallocations net to zero. '
    + 'Checked live on every load and nightly by the oracle, which pages on any breach.'],
  river: ['The river',
    'Where every rupee currently sits: accruing at brands (delivered, not yet settled) → settled awaiting the next '
    + 'morning’s split → split into the month’s building lines → opened as a statement → paid. '
    + 'There is no confirm step: the close files each media owner’s tax invoice itself (24 Sep).'],
};

export default function Explainer({ terms, title = 'Every term on this screen, explained' }) {
  return (
    <details className={s.panel}>
      <summary className={s.panelTitle} style={{ cursor: 'pointer' }}>{title}</summary>
      <div className={s.compGrid} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {terms.map((key) => {
          const t = TERMS[key];
          if (!t) return null;
          return (
            <div key={key} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{t[0]}</div>
              <div style={{ color: 'var(--ink-soft)' }}>{t[1]}</div>
            </div>
          );
        })}
      </div>
    </details>
  );
}
